// dependencies
import { useMemo, useEffect, useState } from 'react'
import _last from 'lodash/last'
import { useQuery } from 'react-query'
// helpers
import { getStoreSlug, getOpen, getExtendedStoreType, generateStoreCrumbs } from '@helpers/store-locator'
import { expandState } from '@helpers/geo-location'
import { getFullRoute } from '@helpers/route'
import { getStoreReviews, getStoreInfo } from '@services/stores'

export const FETCH_PROPERTIES = {
  STORE_REVIEWS: 'STORE_REVIEWS',
}

/**
 * Fetches store info and returns only necessary data
 * @param {number} storeNumber
 * @returns {object}
 */
export const useGetStoreInfo = storeNumber => {
  const { isLoading, isError, data } = useQuery({
    queryKey: ['storeInfo', storeNumber],
    queryFn: () => getStoreInfo(storeNumber),
  })

  if (isLoading || isError) return {}

  return {
    storeTimeZone: data.timeZone,
    storeTemporarilyClosed: data.temporarilyClosed,
    storeOverallScore: data.ratings?.lifetime?.overallScore,
    displayName: data.displayName,
    storeName: data.storeName,
    address1: data.address1,
    city: data.city,
    state: data.state,
    phoneNumber: data.phoneNumber,
    storeSlug: data.storeSlug,
  }
}

/**
 * @param {Args} store
 * @returns {void}
 */
// eslint-disable-next-line import/prefer-default-export
export function useStore({ store }, fetchProperties = []) {
  const [reviews, setReviews] = useState([])

  const storeId = store?.id
  const storeNumber = useMemo(() => store?.StoreNumber || store?.storeNumber, [store])
  const storeName = store?.StoreName || store?.storeName
  const displayName = store?.DisplayName || store?.displayName
  const extendedDisplayName = displayName || storeName
  const storeType = store?.StoreType || store?.storeType
  const storeCity = store?.City || store?.city
  const storeState = store?.State || store?.state
  const storeZip = store?.Zip || store?.zip || store?.zipCode
  const address1 = store?.Address1 || store?.address1
  const address2 = store?.Address2 || store?.address2
  const storeHours = store?.StoreHours || store?.store_hours
  const storeRegularHours = store?.hours?.regularHours
  const distance = store?.distance || store?.Distance
  const storePhoneNumber = store?.PhoneNumber || store?.phoneNumber
  const storeLocation = store?.Location

  const storeRatings = store?.ratings

  const auto = store?.auto

  const storeUrl = useMemo(() => getFullRoute(store?.storeSlug || getStoreSlug(store)), [store])
  const storeExpandedState = useMemo(() => expandState(storeState), [storeState])
  const storeExtendedName = useMemo(
    () => `${storeCity} -${storeName ? ` ${storeName?.replace(storeType, '')}` : ''} ${storeType}`,
    [storeCity, storeName, storeType],
  )
  const storeExtendedLocationAndName = `${storeCity}, ${storeState} ${
    extendedDisplayName ? ` - ${extendedDisplayName}` : ''
  }`
  const storeShortTodayOpenStatus = useMemo(() => getOpen(store, false, true), [store])
  const storeExtendedStoreType = useMemo(() => getExtendedStoreType({ storeType, storeCity, storeState }), [
    storeCity,
    storeState,
    storeType,
  ])
  const breadCrumbs = useMemo(() => generateStoreCrumbs(store), [store])
  const availableInStore = useMemo(() => {
    const availableHere = []
    if (store?.HomeFurnitureAvailable) {
      availableHere.push('Home Furniture')
    }
    if (store?.KidsFurnitureAvailable) {
      availableHere.push('Kids')
    }
    if (store?.PatioFurnitureAvailable) {
      availableHere.push('Patio')
    }
    if (store?.MattressFurnitureAvailable) {
      availableHere.push('Mattress')
    }
    return availableHere
  }, [store])

  useEffect(() => {
    if (storeNumber && fetchProperties?.includes(FETCH_PROPERTIES.STORE_REVIEWS)) {
      getStoreReviews(storeNumber).then(response => {
        const reviewsData = response
        setReviews(reviewsData)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeNumber])

  return {
    storeId,
    storeName,
    storeType,
    storeCity,
    storeState,
    storeZip,
    address1,
    address2,
    distance,
    storeHours,
    storeRegularHours,
    storePhoneNumber,
    storeLocation,
    storeUrl,
    storeExpandedState,
    storeExtendedName,
    storeExtendedLocationAndName,
    storeNumber,
    storeShortTodayOpenStatus,
    breadCrumbs,
    storeExtendedStoreType,
    reviews,
    storeRatings,
    availableInStore,
    auto,
    displayName,
  }
}
